import { useState } from 'react';
import API from '../utils/api';
import toast from 'react-hot-toast';

const FEEDBACK_TAGS = [
  { value: 'quick_responder', label: '⚡ Quick responder', icon: '⚡' },
  { value: 'item_as_described', label: '✅ Item as described', icon: '✅' },
  { value: 'safe_to_meet', label: '🛡️ Safe to meet', icon: '🛡️' },
  { value: 'fair_prices', label: '💰 Fair prices', icon: '💰' },
];

export default function ReviewForm({ referenceId, referenceType, revieweeId, listingId, onSuccess }) {
  const [step, setStep] = useState('rating'); // rating, details, tags, preview
  const [form, setForm] = useState({
    rating: 0,
    title: '',
    body: '',
    feedbackTags: []
  });
  const [submitting, setSubmitting] = useState(false);

  const handleRatingClick = (rating) => {
    setForm(p => ({ ...p, rating }));
    setStep('details');
  };

  const handleToggleTag = (tag) => {
    setForm(p => ({
      ...p,
      feedbackTags: p.feedbackTags.includes(tag)
        ? p.feedbackTags.filter(t => t !== tag)
        : [...p.feedbackTags, tag]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.rating) {
      toast.error('Please select a rating');
      return;
    }

    setSubmitting(true);
    try {
      await API.post('/reviews', {
        referenceId,
        referenceType,
        revieweeId,
        listingId,
        rating: form.rating,
        title: form.title || null,
        body: form.body || null,
        feedbackTags: form.feedbackTags
      });

      toast.success('Review submitted! Thank you for your feedback.');
      onSuccess?.();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ maxWidth: 500, margin: '0 auto' }}>
      {/* Step 1: Rating */}
      {step === 'rating' && (
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: 20, fontWeight: 700, marginBottom: 24 }}>
            How would you rate this seller?
          </h3>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginBottom: 24 }}>
            {[1, 2, 3, 4, 5].map(r => (
              <button
                key={r}
                onClick={() => handleRatingClick(r)}
                style={{
                  fontSize: 40,
                  background: form.rating >= r ? '#FFF3E0' : 'white',
                  border: form.rating >= r ? '2px solid #FF9800' : '1px solid var(--border)',
                  borderRadius: 12,
                  padding: '12px 16px',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  if (form.rating < r) e.target.style.borderColor = '#FFB74D';
                }}
                onMouseLeave={(e) => {
                  if (form.rating < r) e.target.style.borderColor = 'var(--border)';
                }}
              >
                ⭐
              </button>
            ))}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-soft)' }}>
            {form.rating > 0 && ['Poor', 'Fair', 'Good', 'Very Good', 'Excellent'][form.rating - 1]}
          </div>
        </div>
      )}

      {/* Step 2: Title & Review */}
      {step === 'details' && (
        <div style={{ padding: '20px 0' }}>
          <div style={{ marginBottom: 20 }}>
            <label className="form-label">Review title (optional)</label>
            <input
              type="text"
              className="form-input"
              placeholder="e.g., Great experience!"
              value={form.title}
              onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
              maxLength={100}
            />
          </div>

          <div style={{ marginBottom: 20 }}>
            <label className="form-label">Your review (optional)</label>
            <textarea
              className="form-textarea"
              placeholder="Tell others about your experience..."
              value={form.body}
              onChange={e => setForm(p => ({ ...p, body: e.target.value }))}
              style={{ minHeight: 100 }}
              maxLength={500}
            />
            <div style={{ fontSize: 11, color: 'var(--text-soft)', marginTop: 4 }}>
              {form.body.length}/500 characters
            </div>
          </div>

          <button
            onClick={() => setStep('tags')}
            className="btn btn-cherry btn-full"
          >
            Continue →
          </button>
        </div>
      )}

      {/* Step 3: Feedback Tags */}
      {step === 'tags' && (
        <div style={{ padding: '20px 0' }}>
          <h4 style={{ fontWeight: 700, fontSize: 14, marginBottom: 16 }}>
            What stood out? (Select all that apply)
          </h4>

          <div style={{ display: 'grid', gap: 10, marginBottom: 24 }}>
            {FEEDBACK_TAGS.map(tag => (
              <button
                key={tag.value}
                onClick={() => handleToggleTag(tag.value)}
                style={{
                  padding: '12px 16px',
                  textAlign: 'left',
                  border: form.feedbackTags.includes(tag.value)
                    ? '2px solid #4CAF50'
                    : '1px solid var(--border)',
                  background: form.feedbackTags.includes(tag.value)
                    ? '#F1F8E9'
                    : 'white',
                  borderRadius: 10,
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: 14,
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  if (!form.feedbackTags.includes(tag.value)) {
                    e.currentTarget.style.borderColor = '#A5D6A7';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!form.feedbackTags.includes(tag.value)) {
                    e.currentTarget.style.borderColor = 'var(--border)';
                  }
                }}
              >
                {tag.label}
              </button>
            ))}
          </div>

          <button
            onClick={() => setStep('preview')}
            className="btn btn-cherry btn-full"
          >
            Review Submission →
          </button>
        </div>
      )}

      {/* Step 4: Preview & Submit */}
      {step === 'preview' && (
        <form onSubmit={handleSubmit} style={{ padding: '20px 0' }}>
          <div style={{ background: 'var(--cream)', borderRadius: 12, padding: 16, marginBottom: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <span style={{ fontSize: 20 }}>{'⭐'.repeat(form.rating)}</span>
              <button
                type="button"
                onClick={() => setStep('rating')}
                style={{ fontSize: 12, color: 'var(--cherry)', background: 'none', border: 'none', cursor: 'pointer' }}
              >
                Change
              </button>
            </div>

            {form.title && (
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8 }}>
                {form.title}
              </div>
            )}

            {form.body && (
              <div style={{ fontSize: 13, color: 'var(--text-mid)', lineHeight: 1.6, marginBottom: 12 }}>
                {form.body}
              </div>
            )}

            {form.feedbackTags.length > 0 && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {form.feedbackTags.map(tag => {
                  const tagObj = FEEDBACK_TAGS.find(t => t.value === tag);
                  return (
                    <span
                      key={tag}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        padding: '4px 10px',
                        background: 'white',
                        border: '1px solid #4CAF50',
                        borderRadius: 16,
                        fontSize: 12,
                        fontWeight: 600,
                        color: '#2E7D32'
                      }}
                    >
                      {tagObj.icon} {tagObj.label.split(' ').slice(1).join(' ')}
                    </span>
                  );
                })}
              </div>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <button
              type="button"
              onClick={() => setStep('tags')}
              className="btn btn-ghost"
              disabled={submitting}
            >
              ← Back
            </button>
            <button
              type="submit"
              className="btn btn-cherry"
              disabled={submitting}
            >
              {submitting ? '⏳ Submitting...' : '✓ Submit Review'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
